import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, FileSpreadsheet, Sparkles, Send, Store, Clock, BadgeCheck, Newspaper,
  ExternalLink, Check, ShieldCheck, Zap,
} from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import api from "@/lib/api";
import { PublicFooter } from "@/components/PublicShell";
import { VendorLogo } from "@/pages/Marketplace";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const pub = axios.create({ baseURL: API });

const HERO = "https://images.unsplash.com/photo-1758399750112-366c3c804eb7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwY3JhbmUlMjBza3lsaW5lfGVufDB8fHx8MTc4ODc3MDI0N3ww&ixlib=rb-4.1.0&q=85&w=1400";

const STEPS = [
  { icon: FileSpreadsheet, title: "Load your rates — once or per quote",
    desc: "Save your price list as a catalogue for instant matching, or upload one straight from your drive each time you quote. Uploaded price lists are matched in memory and never stored — your pricing stays private." },
  { icon: Sparkles, title: "Matiqs prices it automatically",
    desc: "AI matches every line to your catalogue or an uploaded price list in seconds. You review, override anything, add your terms." },
  { icon: Clock, title: "Send a branded quotation the same hour",
    desc: "Your logo, your reference, page sub-totals — as PDF and priced Excel. Revisions in one click." },
];

const NEWS_SOURCES = [
  { name: "Construction Week Middle East", note: "Awards, contractor news and project updates",
    url: "https://www.constructionweekonline.com/" },
  { name: "Zawya Projects", note: "Regional project tracking and tender coverage",
    url: "https://www.zawya.com/en/projects" },
  { name: "MEED Projects", note: "Pipeline of Gulf projects out for tender",
    url: "https://www.meedprojects.com/" },
  { name: "Etimad — Saudi tenders portal", note: "Government tenders open for bidding",
    url: "https://tenders.etimad.sa/" },
];

const FAQS = [
  { q: "Do I need to change how I price?",
    a: "No. Matiqs reads your existing rate sheet and quotes in your own wording, units and currency." },
  { q: "What does it cost to be listed?",
    a: "Nothing. The Free plan includes a public marketplace profile and 30 automatic quotes every month." },
  { q: "Who sees my rates?",
    a: "Only you. Contractors see the quotation you choose to send — never your catalogue." },
];

export default function ForVendors() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const signedInVendor = user && user.account_type !== "contractor";
  const [params] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [count, setCount] = useState(null);
  const [plans, setPlans] = useState(null);
  const [catEmpty, setCatEmpty] = useState(false);

  const rfqRef = params.get("rfq") || "";
  const from = params.get("from") || "";
  const email = params.get("email") || "";
  const signup = `/login?type=vendor&mode=register${email ? `&email=${encodeURIComponent(email)}` : ""}`;

  useEffect(() => {
    pub.get("/public/vendors").then((r) => {
      const all = r.data.vendors || [];
      const real = all.filter((v) => (v.business_types || []).length > 0 || v.city || v.country);
      setVendors((real.length ? real : all).slice(0, 8));
      setCount(r.data.count ?? all.length);
    }).catch(() => {});
    pub.get("/billing/plans?account_type=vendor").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!signedInVendor) return;
    api.get("/catalogue").then((r) => setCatEmpty((r.data || []).length === 0)).catch(() => {});
  }, [signedInVendor]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="vendors-brand" className="shrink-0">
            <img src="/matiqs-logo.png" alt="Matiqs" className="brand-logo h-8 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {!signedInVendor && (
              <Link to="/marketplace" className="px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">Marketplace</Link>
            )}
            <Link to="/pricing" className="px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">Pricing</Link>
            <Link to="/how-it-works" className="px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">How it works</Link>
          </nav>
          <div className="flex items-center gap-2 shrink-0">
            {signedInVendor ? (
              <>
                <Button variant="outline" data-testid="vendors-catalogue" onClick={() => navigate("/catalogue")} className="h-10 font-semibold">Catalogue</Button>
                <Button data-testid="vendors-dashboard" onClick={() => navigate("/dashboard")} className="h-10 font-semibold gap-2">Go to Quote Builder <ArrowRight className="w-4 h-4" /></Button>
              </>
            ) : (
              <>
                <Button variant="outline" data-testid="vendors-signin" onClick={() => navigate("/login?type=vendor")} className="h-10 font-semibold">Sign in</Button>
                <Button data-testid="vendors-signup-nav" onClick={() => navigate(signup)} className="h-10 font-semibold">Get listed free</Button>
              </>
            )}
          </div>
        </div>
      </header>

      {signedInVendor && catEmpty && (
        <div data-testid="finish-catalogue-prompt" className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-900">
          <div className="max-w-6xl mx-auto px-5 py-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 text-sm font-semibold text-amber-900 dark:text-amber-200">
              <FileSpreadsheet className="w-4 h-4 shrink-0" />
              Finish setting up — your catalogue is empty. Add your rate list so Matiqs can price incoming BOQs automatically, or price any quote from a price list you upload (never stored).
            </div>
            <Button data-testid="finish-catalogue-cta" onClick={() => navigate("/catalogue")}
                    className="h-10 font-semibold gap-2 bg-amber-600 hover:bg-amber-700 text-white">
              Finish your catalogue <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {rfqRef && (
        <div data-testid="rfq-invite-banner" className="bg-brand text-brand-foreground">
          <div className="max-w-6xl mx-auto px-5 py-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm font-semibold">
              {from ? `${from} has invited you to quote ` : "You have been invited to quote "}
              <span className="font-mono font-bold">{rfqRef}</span>
              <span className="font-normal opacity-90"> — create a free account and price it automatically.</span>
            </div>
            <Button data-testid="rfq-invite-cta" onClick={() => navigate(signup)}
                    className="h-10 font-semibold bg-white text-slate-900 hover:bg-white/90">
              Quote this RFQ free <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>
      )}

      {/* ---------------- hero ---------------- */}
      <section data-testid="for-vendors-page" className="border-b border-border bg-secondary/30">
        <div className="max-w-6xl mx-auto px-5 py-16 lg:py-24 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand/30 bg-brand-soft text-brand text-xs font-bold uppercase tracking-wider">
              <Zap className="w-3.5 h-3.5" /> For suppliers, subcontractors & trading firms
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-6 leading-[1.05]">
              Quote in minutes.<br />Win the deal first.
            </h1>
            <p className="text-base md:text-lg text-muted-foreground mt-6 leading-relaxed">
              Matiqs turns a contractor's bill of quantities into your own priced quotation automatically —
              your rates, your format, your logo. The fastest quote usually wins the conversation.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-9">
              {signedInVendor ? (
                <>
                  <Button data-testid="hero-dashboard" onClick={() => navigate("/dashboard")} className="h-12 px-7 text-base font-semibold gap-2">
                    Go to Quote Builder <ArrowRight className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" data-testid="hero-catalogue" onClick={() => navigate("/catalogue")} className="h-12 px-6 text-base font-semibold">
                    Manage catalogue
                  </Button>
                </>
              ) : (
                <>
                  <Button data-testid="hero-signup" onClick={() => navigate(signup)} className="h-12 px-7 text-base font-semibold gap-2">
                    Get listed free <ArrowRight className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" data-testid="hero-pricing" onClick={() => navigate("/pricing")} className="h-12 px-6 text-base font-semibold">
                    See pricing
                  </Button>
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-7 text-sm text-muted-foreground">
              {["30 automatic quotes a month, free", "No card needed", "Free marketplace listing"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-brand" />{t}</span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6, delay: 0.1 }} className="relative">
            <div className="rounded-3xl overflow-hidden border border-border shadow-2xl shadow-slate-900/10">
              <img src={HERO} alt="Construction site at dusk" className="w-full h-[300px] lg:h-[400px] object-cover" />
            </div>
            <div className="absolute -bottom-5 left-5 right-5 bg-card border border-border rounded-2xl px-5 py-4 shadow-xl shadow-slate-900/10">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold">Typical turnaround</div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="font-display text-2xl font-extrabold text-slate-900 dark:text-white">Under 10 minutes</span>
                <span className="text-xs text-muted-foreground">from BOQ to signed-off PDF</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ---------------- steps ---------------- */}
      <section className="max-w-6xl mx-auto px-5 py-20">
        <div className="max-w-2xl">
          <div className="text-[11px] uppercase tracking-[0.16em] text-brand font-bold">Three simple steps</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-3">
            From enquiry to quotation before launch
          </h2>
        </div>
        <div data-testid="vendor-steps" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.07 }}
              className="bg-card border border-border rounded-2xl p-6 hover:border-primary/40 transition-colors"
            >
              <div className="w-10 h-10 rounded-xl bg-brand-soft text-brand flex items-center justify-center">
                <s.icon className="w-5 h-5" />
              </div>
              <div className="font-mono text-xs text-muted-foreground mt-5">Step {i + 1}</div>
              <div className="font-semibold text-slate-900 dark:text-slate-100 mt-1">{s.title}</div>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------------- marketplace listing ---------------- */}
      <section className="border-y border-border bg-secondary/40">
        <div className="max-w-6xl mx-auto px-5 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-brand font-bold">Get found</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-3">
              A marketplace listing that brings the enquiries to you
            </h2>
            <p className="text-base text-muted-foreground mt-5 leading-relaxed">
              Contractors search Matiqs by trade, city and product when they need prices today.
              Your free profile shows your logo, trades, coverage and valid trade licence or ISO
              certificates — so you are shortlisted before the phone calls start.
            </p>
            <ul className="mt-7 space-y-3">
              {[
                "Public company profile with your logo and trades",
                "Verified documents badge — expired papers stay hidden automatically",
                "RFQs arrive with the BOQ and every attachment attached",
                "Silver and Pro plans push your logo higher in search",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <BadgeCheck className="w-4 h-4 text-brand shrink-0 mt-0.5" /> {t}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Button data-testid="listing-signup" onClick={() => navigate(signup)} className="h-11 font-semibold gap-2">
                <Store className="w-4 h-4" /> Create my free listing
              </Button>
              <Link data-testid="browse-marketplace" to="/marketplace" className="text-sm font-bold text-primary hover:underline">
                Browse the marketplace
              </Link>
            </div>
          </div>

          <div className="bg-card border border-border rounded-2xl p-6">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold">Listed right now</div>
              {count !== null && (
                <div data-testid="vendor-count" className="font-mono text-sm font-bold text-primary">{count} companies</div>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-5">
              {vendors.map((v, i) => (
                <Link
                  key={v.id}
                  to={`/vendor/${v.id}`}
                  data-testid={`listed-vendor-${i}`}
                  className="flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary/40 transition-colors"
                >
                  <VendorLogo vendor={v} size={36} />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{v.company_name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {[v.city, v.country].filter(Boolean).join(", ") || v.vendor_type}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- news / projects ---------------- */}
      <section className="max-w-6xl mx-auto px-5 py-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-brand font-bold">
              <Newspaper className="w-3.5 h-3.5" /> Projects to quote
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-3">
              Know which projects are coming before the BOQ arrives
            </h2>
            <p className="text-base text-muted-foreground mt-4 leading-relaxed">
              We are building a weekly feed of awarded packages and live tenders in your trades, right here
              on your dashboard. Until it lands, these are the sources our team watches.
            </p>
          </div>
          <span data-testid="news-coming-soon" className="px-3 py-1.5 rounded-full bg-brand-soft text-brand text-xs font-bold uppercase tracking-wider border border-brand/25">
            Coming soon
          </span>
        </div>

        <div data-testid="news-sources" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {NEWS_SOURCES.map((n, i) => (
            <a
              key={n.name}
              href={n.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`news-source-${i}`}
              className="group bg-card border border-border rounded-2xl p-5 hover:border-primary/40 hover:shadow-lg shadow-slate-900/5 transition-all"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-semibold text-slate-900 dark:text-slate-100 leading-snug">{n.name}</div>
                <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
              </div>
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{n.note}</p>
            </a>
          ))}
        </div>
      </section>

      {/* ---------------- pricing strip ---------------- */}
      <section className="border-y border-border bg-secondary/40">
        <div className="max-w-6xl mx-auto px-5 py-20">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.16em] text-brand font-bold">Pricing</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-3">
              Premium features for free. All features for less.
            </h2>
            <p data-testid="vendor-pricing-tagline" className="text-base md:text-lg font-semibold text-foreground mt-3">
              Powerful free plan. Unbeatable low plans — cheaper than one lost enquiry.
            </p>
          </div>
          <div data-testid="vendor-pricing-strip" className="grid sm:grid-cols-3 gap-5 mt-10">
            {(plans?.vendor_plans || []).map((p) => (
              <div key={p.id} data-testid={`vendor-plan-${p.id}`}
                   className={`rounded-2xl border p-6 bg-card ${p.id === "pro" ? "border-brand/50" : "border-border"}`}>
                <div className="font-display text-lg font-bold text-slate-900 dark:text-slate-100">{p.name}</div>
                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="font-display text-3xl font-extrabold tabular-nums">
                    {p.price === 0 ? "Free" : `${plans.currency} ${p.price}`}
                  </span>
                  {p.price > 0 && <span className="text-sm text-muted-foreground">/ month</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {p.quota === null ? "Unlimited automatic quotes" : `${p.quota} automatic quotes a month`}
                </div>
                {p.price_yearly > 0 && (
                  <div className="text-xs font-semibold text-brand mt-1">
                    or {plans.currency} {p.price_yearly}/year — save 20%
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Button data-testid="pricing-strip-signup" onClick={() => navigate(signup)} className="h-11 font-semibold gap-2">
              Start free <ArrowRight className="w-4 h-4" />
            </Button>
            <Link to="/pricing" className="text-sm font-bold text-primary hover:underline">Compare every plan</Link>
          </div>
        </div>
      </section>

      {/* ---------------- faq + final cta ---------------- */}
      <section className="max-w-6xl mx-auto px-5 py-20 grid lg:grid-cols-2 gap-12">
        <div data-testid="vendor-faqs" className="space-y-6">
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            Straight answers
          </h2>
          {FAQS.map((f) => (
            <div key={f.q}>
              <div className="font-semibold text-slate-900 dark:text-slate-100">{f.q}</div>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="bg-card border border-border rounded-2xl p-8 flex flex-col justify-center">
          <ShieldCheck className="w-8 h-8 text-brand" />
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-5">
            {rfqRef ? `Quote ${rfqRef} in minutes` : "Your next enquiry could be priced in minutes"}
          </h2>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
            Upload your rate list, get listed on the marketplace and let Matiqs handle the arithmetic.
            No card, no setup fee, cancel whenever you like.
          </p>
          <Button data-testid="final-signup" onClick={() => navigate(signup)} className="h-12 mt-7 text-base font-semibold gap-2">
            Get listed free <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
